import React, { useState, useRef } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useUpdateDealStatus } from 'hooks/useDealData'
import { useNotificationStore } from 'store/useNotificationStore'
import { useDynamicManageStore } from 'store/useDynamicManageStore'
import { pdsAnalytics } from 'analytics/pdsAnalytics'
import { useTranslation } from 'react-i18next'
import AcceptSpinner from 'components/AcceptSpinner'
import { IS_NEW_LAYOUT_ENABLED } from 'constants/layoutConstant'

const Footer = () => {
    const { t } = useTranslation()
    const onBack = useDynamicManageStore((state) => state.onBack)
    const { mutateAsync: updatePdsStatus } = useUpdateDealStatus()
    const showNotification = useNotificationStore((state) => state.showNotification)
    const [isLoading, setIsLoading] = useState(false)
    const nodeRef = useRef(null)
    const containerRef = useRef(null)

    // Medallia triggers
    const checkSchedulingCompleted = () => {
        try {
            window.pdsAccepted = true
            window.KAMPYLE_ONSITE_SDK.updatePageView()
        } catch (error) {
            console.error('the KAMPYLE_ONSITE_SDK is not defined or failed to update the page view', error)
        }
    }

    const onAccept = async () => {
        pdsAnalytics.track('accept')
        setIsLoading(true)
        try {
            await updatePdsStatus('Accept')
            checkSchedulingCompleted()
            setIsLoading(false)
            showNotification({
                title: t('accepted_pds'),
                content: t('print_pds_confirmation'),
                show: true
            })
        } catch (error) {}
    }

    const onReject = async () => {
        pdsAnalytics.track('reject')
        try {
            await updatePdsStatus('Reject')
            onBack()
        } catch (error) {}
    }

    return (
        <>
            {isLoading && IS_NEW_LAYOUT_ENABLED && <AcceptSpinner />}

            <Box
                component="footer"
                position="sticky"
                ref={nodeRef}
                sx={{
                    backgroundColor: 'background.default',
                    borderTop: (theme) => `1px solid ${theme.palette.common.gray.dark}`,
                    boxShadow: { xs: '0px 0px 10px 0px rgba(0, 0, 0, 0.03)', md: 'none' },
                    bottom: 0,
                    p: {
                        xs: '0px 12px',
                        smd: '0px'
                    },
                    transition: '.3s all',
                    overflow: 'hidden',
                    zIndex: 2
                }}
            >
                <Box
                    sx={{
                        display: { xs: 'flex', smd: 'grid' },
                        flexDirection: { xs: 'column', smd: 'row' },
                        alignItems: 'center'
                    }}
                >
                    <Box
                        ref={containerRef}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            flexDirection: { xs: 'column', smd: 'row' },
                            gap: 4,
                            order: 2,
                            textAlign: 'center',
                            py: 4,
                            pr: { xs: 0, smd: 7, md: 14 }
                        }}
                    >
                        <Typography
                            letterSpacing="1px"
                            color="common.charcoal.main"
                            data-testid="footer-text"
                            sx={{
                                typography: { xs: 'boldBody1', smd: 'boldSubTitle' },
                                letterSpacing: 0
                            }}
                        >
                            {t('does_everything_look_correct')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'stretch',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                gap: 4,
                                width: { xs: '100%', smd: 'initial' }
                            }}
                        >
                            <Button
                                variant="outlined"
                                disableRipple
                                onClick={onReject}
                                data-testid="reject"
                                sx={{
                                    p: '12px 14px',
                                    height: '40px',
                                    minWidth: { xs: '150px', sm: '155px' }
                                }}
                            >
                                {t('no_notify_dealer')}
                            </Button>
                            <Button
                                variant="contained"
                                disableRipple
                                onClick={onAccept}
                                data-testid="accept"
                                sx={{
                                    p: '12px 14px',
                                    height: '40px',
                                    minWidth: { xs: '150px', sm: '155px' },
                                    backgroundColor: 'common.link.main'
                                }}
                            >
                                {t('yes')}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    )
}

export default Footer
